import { useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_Literature from "../config_Literature";

/* ─── inline styles as JS objects ─── */
const S = {
  root: {
    fontFamily: "'DM Sans', sans-serif",
    display: "flex",
    flexDirection: "column",
  },
  hero: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  card: {
    width: "100%",
    maxWidth: 760,
    background: "#fff",
    borderRadius: 20,
    border: "1px solid #e2e8f8",
    boxShadow: "0 8px 40px rgba(30,58,138,0.08)",
    padding: "1.75rem",
    marginBottom: "2rem",
  },
  divider: {
    height: 1,
    background: "#f1f5f9",
    margin: "1.25rem 0",
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    marginBottom: 6,
    display: "block",
  },
  input: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    fontWeight: 500,
    color: "#0f172a",
    background: "#f8fafc",
    border: "1.5px solid #e2e8f8",
    borderRadius: 10,
    padding: "10px 14px",
    outline: "none",
    width: "100%",
    height: 46,
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  select: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    fontWeight: 500,
    color: "#0f172a",
    background: "#f8fafc",
    border: "1.5px solid #e2e8f8",
    borderRadius: 10,
    padding: "10px 32px 10px 14px",
    outline: "none",
    width: "100%",
    height: 46,
    appearance: "none",
    WebkitAppearance: "none",
    cursor: "pointer",
  },
  selectWrap: { position: "relative", width: "100%" },
  selectArrow: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    width: 0,
    height: 0,
    borderLeft: "4px solid transparent",
    borderRight: "4px solid transparent",
    borderTop: "5px solid #94a3b8",
  },
  toggle: {
    display: "flex",
    background: "#f1f5f9",
    borderRadius: 10,
    border: "1.5px solid #e2e8f8",
    padding: 3,
    gap: 3,
    height: 46,
    alignItems: "center",
  },
  toggleBtnBase: {
    flex: 1,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    fontWeight: 600,
    border: "none",
    background: "transparent",
    borderRadius: 7,
    padding: "0 14px",
    height: "100%",
    cursor: "pointer",
    color: "#64748b",
    transition: "all 0.2s",
    whiteSpace: "nowrap",
  },
  toggleBtnActive: {
    background: "linear-gradient(135deg,#1e3a8a,#3b82f6)",
    color: "#fff",
    boxShadow: "0 2px 8px rgba(59,130,246,0.3)",
  },
  searchBtn: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    fontWeight: 700,
    background: "linear-gradient(135deg,#1e3a8a 0%,#3b82f6 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "0 24px",
    height: 46,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 4px 16px rgba(59,130,246,0.3)",
    whiteSpace: "nowrap",
    width: "100%",
    justifyContent: "center",
  },
  bottomRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  bottomLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    whiteSpace: "nowrap",
  },
  chips: { display: "flex", gap: 8, flexWrap: "wrap" },
  chipBase: {
    fontSize: 12,
    fontWeight: 600,
    border: "1.5px solid #e2e8f8",
    borderRadius: 100,
    padding: "5px 14px",
    cursor: "pointer",
    color: "#64748b",
    background: "#fff",
    fontFamily: "'DM Sans', sans-serif",
    transition: "all 0.15s",
  },
  chipActive: {
    borderColor: "#3b82f6",
    color: "#1e3a8a",
    background: "#eff6ff",
  },
  stats: {
    display: "flex",
    gap: "2rem",
    justifyContent: "center",
    marginTop: "2.5rem",
    flexWrap: "wrap",
  },
  statNum: {
    fontSize: 22,
    fontWeight: 800,
    color: "#1e3a8a",
    letterSpacing: "-0.5px",
  },
  statLbl: {
    fontSize: 12,
    color: "#94a3b8",
    fontWeight: 500,
    marginTop: 2,
  },
  loadingWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    marginTop: "2.5rem",
  },
  loadingText: { fontSize: 14, color: "#64748b", fontWeight: 500 },
};

/* ─── responsive grid via CSS-in-JS injection ─── */
const RESPONSIVE_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  .am-grid {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 12px;
    align-items: end;
  }
  .am-keyword-field { grid-column: 1 / -1; }

  @keyframes am-spin { to { transform: rotate(360deg); } }
  @keyframes am-pulse {
    0%,100% { opacity:1; transform:scale(1); }
    50%      { opacity:0.5; transform:scale(0.8); }
  }
  .am-eyebrow-dot { animation: am-pulse 2s infinite; }
  .am-spinner {
    width: 40px; height: 40px;
    border: 3px solid #dbeafe;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: am-spin 0.8s linear infinite;
  }
  .am-search-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(59,130,246,0.4) !important; }
  .am-search-btn:active { transform: translateY(0); }
  .am-input:focus, .am-select:focus {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.12) !important;
    background: #fff !important;
  }
  .am-chip:hover { border-color:#93c5fd !important; color:#1e3a8a !important; background:#eff6ff !important; }

  @media (max-width: 640px) {
    .am-card { padding: 1.25rem !important; border-radius: 16px !important; }
    .am-grid { grid-template-columns: 1fr !important; }
    .am-keyword-field { grid-column: 1 !important; }
    .am-stats { gap: 1.5rem !important; }
  }
  @media (min-width: 641px) and (max-width: 900px) {
    .am-grid { grid-template-columns: 1fr 1fr !important; }
    .am-keyword-field { grid-column: 1 / -1 !important; }
    .am-search-col { grid-column: 1 / -1; }
  }
`;

const POPULAR = [
  { label: "mRNA vaccines", query: "mRNA vaccine immunogenicity" },
  { label: "Alzheimer's", query: "Alzheimer neurodegeneration biomarkers" },
  { label: "AMR", query: "antibiotic resistance mechanisms" },
  { label: "AI radiology", query: "deep learning radiology imaging" },
];

export default function ArticleMiner() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [yearRange, setYearRange] = useState("5");
  const [mode, setMode] = useState("A");
  const [loading, setLoading] = useState(false);
  const [activeChip, setActiveChip] = useState(null);

  // inject responsive CSS once
  if (
    typeof document !== "undefined" &&
    !document.getElementById("am-styles")
  ) {
    const style = document.createElement("style");
    style.id = "am-styles";
    style.textContent = RESPONSIVE_CSS;
    document.head.appendChild(style);
  }

  const handleChip = (chip) => {
    setActiveChip(chip.label);
    setQuery(chip.query);
  };

  const handleSearch = async () => {
    if (!query.trim()) return alert("Enter search keywords");
    setLoading(true);

    try {
      // 1️⃣ Trigger job
      const requestBody = { query, year_choice: yearRange, mode };
      console.log("🚀 Sending job request:", requestBody);

      const res = await fetch(`${API_BASE_Literature}/api/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const resJson = await res.json();
      console.log("📤 /api/analyze response:", resJson);

      const { job_id } = resJson;
      if (!job_id) throw new Error("Failed to start job");

      // 2️⃣ Poll for result
      const poll = async () => {
        console.log(`⏳ Polling for job_id: ${job_id} ...`);
        const r = await fetch(
          `${API_BASE_Literature}/api/result?job_id=${job_id}`,
        );
        const data = await r.json();
        console.log("📥 /api/result response:", data);

        if (data.status === "running") {
          setTimeout(poll, 3000); // Poll again in 3 seconds
        } else if (data.status === "ready") {
          console.log("✅ Job complete! Navigating to results...");
          navigate("/article/results", {
            state: { query, yearRange, mode, result: data.result },
          });
        } else {
          console.error("⚠️ Unexpected status:", data);
          alert("Unexpected job status. Check console.");
          setLoading(false);
        }
      };

      poll();
    } catch (err) {
      console.error("❌ Error in handleSearch:", err);
      alert(err.message || "Search error");
      setLoading(false);
    }
  };

  return (
    <div style={S.root}>
      <main style={S.hero}>
        <h1
          style={{
            fontSize: "clamp(2rem,5vw,3rem)",
            fontWeight: 800,
            color: "#0f172a",
            letterSpacing: "-1.5px",
            lineHeight: 1.1,
            marginBottom: "1rem",
          }}
        >
          Literature Search
          <br />
          <span
            style={{
              background: "linear-gradient(90deg,#1e3a8a,#3b82f6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            at research speed
          </span>
        </h1>

        {/* <p
          style={{
            fontSize: 15,
            color: "#64748b",
            maxWidth: 480,
            lineHeight: 1.65,
            marginBottom: "2.5rem",
          }}
        >
          Surface the most relevant biomedical papers instantly. Filter by
          recency, rank by impact.
        </p> */}

        {/* ── CARD ── */}
        <div style={S.card} className="am-card">
          <div className="am-grid">
            {/* Keyword */}
            <div className="am-keyword-field">
              <label style={S.label}>Keywords / Title</label>
              <input
                className="am-input"
                style={S.input}
                type="text"
                placeholder="e.g. CRISPR gene editing off-target effects"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>

            {/* Year Range */}
            <div>
              <label style={S.label}>Year range</label>
              <div style={S.selectWrap}>
                <select
                  className="am-select"
                  style={S.select}
                  value={yearRange}
                  onChange={(e) => setYearRange(e.target.value)}
                >
                  <option value="1">Last 1 year</option>
                  <option value="5">Last 5 years</option>
                  <option value="10">Last 10 years</option>
                </select>
                <span style={S.selectArrow} />
              </div>
            </div>

            {/* Mode Toggle */}
            <div>
              <label style={S.label}>Mode</label>
              <div style={S.toggle}>
                {[
                  { val: "A", label: "Fast" },
                  { val: "B", label: "Impact" },
                ].map((m) => (
                  <button
                    key={m.val}
                    style={{
                      ...S.toggleBtnBase,
                      ...(mode === m.val ? S.toggleBtnActive : {}),
                    }}
                    onClick={() => setMode(m.val)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Search Button */}
            <div className="am-search-col">
              <label style={{ ...S.label, visibility: "hidden" }}>go</label>
              <button
                className="am-search-btn"
                style={S.searchBtn}
                onClick={handleSearch}
                disabled={loading}
              >
                <svg
                  width="15"
                  height="15"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                {loading ? "Searching…" : "Search"}
              </button>
            </div>
          </div>

          <div style={S.divider} />

          {/* Quick chips */}
          <div style={S.bottomRow}>
            <span style={S.bottomLabel}>Popular:</span>
            <div style={S.chips}>
              {POPULAR.map((c) => (
                <button
                  key={c.label}
                  className="am-chip"
                  style={{
                    ...S.chipBase,
                    ...(activeChip === c.label ? S.chipActive : {}),
                  }}
                  onClick={() => handleChip(c)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div style={S.loadingWrap}>
            <div className="am-spinner" />
            <span style={S.loadingText}>Fetching PubMed records…</span>
          </div>
        )}

        {/* Stats */}
        {/* <div style={S.stats} className="am-stats">
          {[
            { num: "38M+", lbl: "Citations indexed" },
            { num: "< 8s", lbl: "Avg. response time" },
            { num: "200+", lbl: "Journals covered" },
          ].map((s) => (
            <div key={s.lbl} style={{ textAlign: "center" }}>
              <div style={S.statNum}>{s.num}</div>
              <div style={S.statLbl}>{s.lbl}</div>
            </div>
          ))}
        </div> */}
      </main>
    </div>
  );
}
